import {
  Component,
  signal,
  ChangeDetectionStrategy,
  output,
  input,
  viewChild,
  HostListener,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { Popover, PopoverModule } from 'primeng/popover';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { CloseOverlayOnNavigationDirective } from '@tourfold/libraries/shared-ui';

export interface SmsReportFilterValues {
  fromDate: Date | null;
  toDate: Date | null;
  status: string | null;
}

@Component({
  selector: 'tf-settings-sms-report-filters',
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    ButtonModule,
    SelectModule,
    PopoverModule,
    TranslateModule,
    TooltipModule,
    CloseOverlayOnNavigationDirective,
  ],
  templateUrl: './sms-report-filters.html',
  styleUrl: './sms-report-filters.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmsReportFilters {
  // Inputs
  readonly fromDate = input<Date | null>(null);
  readonly toDate = input<Date | null>(null);
  readonly status = input<string | null>(null);
  readonly hasActiveFilters = input<boolean>(false);
  readonly isLoading = input<boolean>(false);

  // Outputs
  readonly filterApplied = output<SmsReportFilterValues>();
  readonly filterCleared = output();
  readonly refresh = output<void>();

  // View children
  readonly filterPopover = viewChild<Popover>('filterPopover');

  // Local state
  readonly localFromDate = signal<Date | null>(null);
  readonly localToDate = signal<Date | null>(null);
  readonly localStatus = signal<string | null>(null);
  readonly viewportWidth = signal(window.innerWidth);

  // Date limits (28 days ago to today)
  readonly minDate = new Date(new Date().setDate(new Date().getDate() - 28));
  readonly maxDate = new Date();

  readonly statusOptions = [
    { label: 'All', value: null },
    { label: 'Scheduled', value: 'SCHEDULED' },
    { label: 'Sending', value: 'SENDING' },
    { label: 'Sent', value: 'SENT' },
    { label: 'Delivered', value: 'DELIVERED' },
    { label: 'Failed', value: 'FAILED' },
  ];

  constructor() {
    // Sync input signals to local signals
    effect(() => {
      this.localFromDate.set(this.fromDate());
      this.localToDate.set(this.toDate());
      this.localStatus.set(this.status());
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.viewportWidth.set(window.innerWidth);
  }

  get isDesktopView(): boolean {
    return this.viewportWidth() >= 1500;
  }

  get isTabletView(): boolean {
    return this.viewportWidth() < 1500;
  }

  onFromDateChange(date: Date | null): void {
    this.localFromDate.set(date);
  }

  onToDateChange(date: Date | null): void {
    this.localToDate.set(date);
  }

  onStatusChange(status: string | null): void {
    this.localStatus.set(status);
  }

  applyFilters(): void {
    this.filterApplied.emit({
      fromDate: this.localFromDate(),
      toDate: this.localToDate(),
      status: this.localStatus(),
    });
    this.filterPopover()?.hide();
  }

  clearFilters(): void {
    this.localFromDate.set(null);
    this.localToDate.set(null);
    this.localStatus.set(null);
    this.filterCleared.emit();
    this.filterPopover()?.hide();
  }

  toggleFilterPopover(event: Event): void {
    this.filterPopover()?.toggle(event);
  }
}
